import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as chatApi from "./api";

/** Open (creating if needed) a ticket's chat room. Returns roomId + initial
 * state; live message updates come from useRoomMessages, whose key the
 * WebSocket invalidates. */
export function useChatRoom(ticketId: string | null) {
  return useQuery({
    queryKey: ["chat", "room-for-ticket", ticketId],
    queryFn: () => chatApi.openRoom(ticketId as string),
    enabled: ticketId !== null,
  });
}

/** The room's messages, keyed by roomId so the WebSocket's
 * `invalidate(["chat", roomId])` refetches it — the DB is the source of truth,
 * nothing is merged client-side.
 *
 * The WebSocket is the primary "new message" signal, but it can be silently
 * broken by a corporate proxy, antivirus, or a flaky network — in which case
 * chat would appear frozen. So while a chat is open we also poll every few
 * seconds as a fallback: near-live updates even when the socket is down, and no
 * cost once the panel closes (the query goes inactive and polling stops). Focus
 * refetch covers the "came back to the tab" case instantly. */
export function useRoomMessages(roomId: string | null, initial: chatApi.ChatMessage[]) {
  return useQuery({
    queryKey: ["chat", roomId],
    queryFn: () => chatApi.roomMessages(roomId as string),
    enabled: roomId !== null,
    initialData: roomId ? initial : undefined,
    refetchInterval: roomId ? 5000 : false,
    refetchOnWindowFocus: true,
  });
}

export function usePostMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, body }: { roomId: string; body: Parameters<typeof chatApi.postMessage>[1] }) =>
      chatApi.postMessage(roomId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["chat", vars.roomId] });
    },
  });
}

export function useMarkChatRead() {
  return useMutation({ mutationFn: (roomId: string) => chatApi.markRead(roomId) });
}

/** Debounced typing signal — one ping at most every 2s while typing. */
export function useTypingSignal(roomId: string | null) {
  const last = useRef(0);
  return () => {
    if (!roomId) return;
    const now = Date.now();
    if (now - last.current < 2000) return;
    last.current = now;
    void chatApi.signalTyping(roomId).catch(() => {});
  };
}

/** Show "X is typing…" for a moment when a typing event arrives for this room. */
export function useTypingIndicator(roomId: string | null): string | null {
  const [who, setWho] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!roomId) return;
    function onTyping(e: Event) {
      const detail = (e as CustomEvent).detail as { room_id: string; user_name: string };
      if (detail.room_id !== roomId) return;
      setWho(detail.user_name);
      window.clearTimeout(timer.current);
      // Typing pings arrive every ~2s; clear a little after that so the
      // indicator disappears when they stop.
      timer.current = window.setTimeout(() => setWho(null), 3500);
    }
    window.addEventListener("chat:typing", onTyping);
    return () => {
      window.removeEventListener("chat:typing", onTyping);
      window.clearTimeout(timer.current);
    };
  }, [roomId]);

  return who;
}
